<template>
  <div v-if="!!phaseData">
    <field-label v-if="isAtopicDermatite && pharmaceuticalDomainConfig.SUMMARIZEDFLOW">
      <radio-options
        class="mt-4"
        label="O paciente já realizou sessões de fototerapia?"
        :options="defaultYesNoOptions"
        v-model="phaseData['realizou_sessoes_fototerapia'].value"
        row
        hide-details
        @input="phaseData['numero_sessoes_fototerapia'].value = null"
        clearable
        :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
        :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
      />
      <text-field
        type="number"
        label="Quantas sessões de fototerapia foram realizadas?"
        :mask="['###']"
        v-if="phaseData['realizou_sessoes_fototerapia'].value"
        v-model="phaseData['numero_sessoes_fototerapia'].value"
        class="mt-2"
        dense
        hide-details
        :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
      />
    </field-label>

    <v-sheet v-if="loading || error" class="d-flex flex-column align-center justify-center row-gap-2" height="50vh">
      <template v-if="error">
        <h3>Um erro ocorreu ao tentar carregar os dados da tela!</h3>
        <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
          >tentar novamente <v-icon class="ml-2" large>mdi-refresh</v-icon></v-btn
        >
      </template>
      <template v-else>
        <h3>Carregando dados...</h3>
        <v-progress-circular indeterminate />
      </template>
    </v-sheet>
    <field-label v-else :label="'Selecione os tratamentos prévios utilizados:'">
      <div v-for="key in sortedCategoryKeys" :key="`medicine-category-${key}`" class="mt-3">
        <v-expansion-panels
          v-if="medicineCategoriesRemoved.indexOf(medicinesByCategory[key].category.id) === -1"
          accordion
          class="mb-3"
        >
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-row no-gutters>
                <v-col>
                  {{ medicinesByCategory[key].category.name }}
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <checkbox-drawer
                class="my-1"
                v-for="medicine in medicinesByCategory[key].medicines"
                :key="`medicine-${medicine.id}`"
                :value="!!phaseData.medicines[medicine.id]"
                :label="medicine.name"
                @input="toggleMedicine(medicine, $event)"
              >
                <v-form ref="addPeriodForm" v-model="periodModels[medicine.id].valid" class="d-flex align-center gap-2">
                  <v-container>
                    <v-row dense>
                      <v-col class="py-0">
                        <text-field
                          style="width: 100%"
                          type="number"
                          label="Dose máxima (mg)"
                          v-model="periodModels[medicine.id].dosage"
                          :rules="[$validation.minValue(0, false)]"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <select-field
                          style="width: 100%"
                          :items="defaultPeriodOptions"
                          label="Período"
                          v-model="periodModels[medicine.id].period"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <text-field
                          style="width: 100%"
                          label="Núm. semanas"
                          :mask="['##']"
                          v-model="periodModels[medicine.id].length"
                          v-if="periodModels[medicine.id].period === 'CADA_N_SEMANAS'"
                          :rules="[$validation.minValue(0, false)]"
                        />
                      </v-col>
                    </v-row>
                    <v-row dense>
                      <v-col class="py-0">
                        <text-field
                          style="width: 100%"
                          :mask="['##/##/####']"
                          label="Início"
                          v-model="periodModels[medicine.id].start"
                          :rules="[$validation.validDate(), $validation.dateBefore(null, true)]"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <text-field
                          style="width: 100%"
                          :mask="['##/##/####']"
                          label="Fim"
                          v-model="periodModels[medicine.id].end"
                          :rules="[$validation.validDate(), $validation.dateBefore(null, true)]"
                        />
                      </v-col>
                      <v-col class="py-0">
                        <select-field
                          style="width: 100%"
                          label="Motivo"
                          :items="stopReasons"
                          item-text="name"
                          item-value="id"
                          v-model="periodModels[medicine.id].stopReason"
                          return-object
                          v-if="!!periodModels[medicine.id].end"
                          multiple
                          required
                        />
                      </v-col>
                      <v-col class="py-0">
                        <text-field
                          label="Outros motivos"
                          v-model="periodModels[medicine.id].otherStopReason"
                          v-if="periodModels[medicine.id].stopReason.some((r) => r.name === 'Outros')"
                        />
                      </v-col>
                    </v-row>
                    <v-row dense justify="center">
                      <v-col class="py-0 d-flex" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
                        <span class="text-body-2">
                          Obs.: deixe a data final em branco quando se tratar de medicamento em uso
                        </span>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="py-0">
                        <field-label label="Evento adversos">
                          <v-combobox
                            :items="adverseEffects"
                            multiple
                            outlined
                            dense
                            chips
                            v-model="periodModels[medicine.id].event"
                          />
                        </field-label>
                      </v-col>
                      <v-col class="py-0" v-if="periodModels[medicine.id].event">
                        <text-field
                          style="width: 100%"
                          :mask="['##/##/####']"
                          label="Data do evento"
                          v-model="periodModels[medicine.id].eventDate"
                          :rules="[
                            $validation.validDate(),
                            $validation.dateAfter(periodModels[medicine.id].start, true),
                            $validation.dateBefore(periodModels[medicine.id].end, true),
                          ]"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-spacer />
                      <v-btn
                        class="mt-1"
                        outlined
                        color="primary"
                        small
                        @click="addPeriod(medicine.id)"
                        :disabled="!periodModels[medicine.id].valid"
                        >adicionar período <v-icon small class="ml-2">mdi-plus-circle-outline</v-icon></v-btn
                      >
                    </v-row>
                  </v-container>
                </v-form>
                <field-label
                  class="mt-2"
                  label="Períodos registrados"
                  v-if="phaseData.medicines[medicine.id] && phaseData.medicines[medicine.id].periods.length > 0"
                >
                  <ul class="py-2">
                    <li
                      v-for="(period, periodIndex) in phaseData.medicines[medicine.id].periods"
                      :key="`period-${medicine.id}-${period.start}`"
                    >
                      {{ buildPeriodString(period) }}
                      <v-btn
                        icon
                        small
                        color="error"
                        title="Remover período"
                        @click="removePeriod(medicine.id, periodIndex)"
                        >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </li>
                  </ul>
                </field-label>
              </checkbox-drawer>
              <v-form class="mt-4" ref="addMedicineForm" v-model="medicineModels[key].valid">
                <div class="d-flex align-center gap-2" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
                  <text-field
                    class="flex-grow-1"
                    :label="`Adicionar novo medicamento do tipo ${medicinesByCategory[key].category.name} à lista`"
                    placeholder="Nome do medicamento"
                    :max-length="60"
                    v-model="medicineModels[key].name"
                  />
                  <v-btn
                    class="mt-12"
                    outlined
                    color="primary"
                    small
                    @click="addMedicine(key)"
                    :disabled="!medicineModels[key].valid || !medicineModels[key].name"
                  >
                    adicionar medicamento<v-icon small class="ml-2">mdi-plus-circle-outline</v-icon>
                  </v-btn>
                </div>
              </v-form>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </field-label>
    <field-label class="mt-8" label="Anotações">
      <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
    </field-label>
  </div>
</template>
<script>
import CheckboxDrawer from '@/components/CheckboxDrawer.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import SelectField from '@/components/SelectField.vue'
import TextArea from '@/components/TextArea.vue'
import TextField from '@/components/TextField.vue'
import { TREATMENT_EXHIBITION_RULES } from '@/store/attendance/config/treatmentExhibitionConfig'
import BasePhase from './BasePhase.vue'

import { DISEASES } from '@/constants/disease'
import { MEDICINE_CATEGORIES, MEDICINE_CATEGORIES_DA_SUMMARIZED_REMOVED_IDS } from '@/constants/medicine'

import { ECustomSummmarizedflow } from '@/constants/customSummarizedFlow'
import { v4 as uuidv4, validate as uuidValidate } from 'uuid'
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

export default {
  components: { FieldLabel, TextField, SelectField, CheckboxDrawer, TextArea, RadioOptions },
  extends: BasePhase,
  data() {
    return {
      loading: false,
      error: false,
      medicines: [],
      stopReasons: [],
      periodModels: {},
      medicineModels: {},
      adverseEffects: [],
      treatments: [],
    }
  },
  computed: {
    ...attendanceGettersMap(['attendanceDiseaseId', 'attendance']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),

    isAtopicDermatite() {
      return this.attendance.disease.id === DISEASES.ATOPIC_DERMATITIS
    },
    defaultYesNoOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    medicineCategoriesRemoved() {
      if (
        this.isAtopicDermatite &&
        this.attendance.data.summarizedFlow &&
        this.pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW != ECustomSummmarizedflow.SANOFI
      ) {
        return MEDICINE_CATEGORIES_DA_SUMMARIZED_REMOVED_IDS
      }
      return []
    },
    defaultPeriodOptions() {
      return [
        { text: 'por semana', value: 'POR_SEMANA' },
        { text: 'por dia', value: 'POR_DIA' },
        { text: 'a cada (n) semanas', value: 'CADA_N_SEMANAS' },
      ]
    },

    medicinesByCategory() {
      const medicinesIdsInclude = this.getMedicinesIdsIncludes()

      const medicinesByCategory = {}
      for (const medicine of this.medicines) {
        if (medicinesIdsInclude.includes(medicine.id) || this.isCustomMedicine(medicine)) {
          if (!medicinesByCategory[medicine.category.id]) {
            medicinesByCategory[medicine.category.id] = {
              category: medicine.category,
              medicines: [],
            }
          }
          medicinesByCategory[medicine.category.id].medicines.push(medicine)
        }
      }

      return medicinesByCategory
    },
    sortedCategoryKeys() {
      const order = {}
      order[MEDICINE_CATEGORIES.TOPICOS] = 1
      order[MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS] = 2
      order[MEDICINE_CATEGORIES.IMUNOBIOLOGICOS] = 3
      return Object.entries(this.medicinesByCategory)
        .sort((entry1, entry2) => order[entry1[1].category.id] - order[entry2[1].category.id])
        .map((entry) => entry[0])
    },
  },
  methods: {
    isCustomMedicine(medicine) {
      return uuidValidate(medicine.id)
    },
    getMedicinesIdsIncludes() {
      const treatmentsFiltered = this.treatments.filter((treatment) => {
        const flowExclusion = treatment.exhibitionConfig?.flowExclusion || []

        if (
          !this.attendance.data.summarizedFlow &&
          this.pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW != ECustomSummmarizedflow.SANOFI
        ) {
          return true
        }

        if (!flowExclusion.includes('show_on_treatments_sanofi_phase')) {
          return false
        }
        return TREATMENT_EXHIBITION_RULES[treatment.disease.id].flowExclusion['show_on_treatments_sanofi_phase'].check(
          this.attendance,
        )
      })

      const medicineIds = treatmentsFiltered.map((treatment) => {
        return treatment.medicine.id
      })

      return medicineIds
    },
    getDefaultData() {
      const data = {
        medicines: {},
        realizou_sessoes_fototerapia: { value: null },
        numero_sessoes_fototerapia: { value: null },
      }
      return data
    },
    addPeriod(medicineId) {
      const todayIso = this.$moment().format('YYYY-MM-DD')
      const start = this.$util.toIsoDate(this.periodModels[medicineId].start)
      const end = this.$util.toIsoDate(this.periodModels[medicineId].end)

      const overlaps = this.phaseData.medicines[medicineId].periods.some((period) =>
        this.$util.periodsOverlap(start, end || todayIso, period.start, period.end || todayIso),
      )

      if (overlaps) {
        this.$root.warn('O período informado se sobrepõe a outro período já informado. Verifique!')
        return
      }

      this.phaseData.medicines[medicineId].periods.push({
        dosage: parseInt(this.periodModels[medicineId].dosage) || null,
        stopReason: this.periodModels[medicineId].end
          ? this.periodModels[medicineId].stopReason
              .map((r) => (r.name === 'Outros' ? this.periodModels[medicineId].otherStopReason : r.name))
              .join(', ')
          : null,
        period: this.periodModels[medicineId].period,
        events: {
          selected: this.periodModels[medicineId].event,
          date: this.periodModels[medicineId].eventDate,
        },
        length:
          this.periodModels[medicineId].period === 'CADA_N_SEMANAS'
            ? parseInt(this.periodModels[medicineId].length) || 0
            : null,
        start,
        end,
      })
      this.phaseData.medicines[medicineId].periods.sort(this.$util.objectSorter((period) => period.start))
      this.periodModels[medicineId] = {
        ...this.genNewPeriodModel(),
        dosage: this.periodModels[medicineId].dosage,
        period: this.periodModels[medicineId].period,
        length: this.periodModels[medicineId].period === 'CADA_N_SEMANAS' ? this.periodModels[medicineId].length : null,
      }

      this.$refs.addPeriodForm.forEach((form) => form.resetValidation())
    },
    removePeriod(medicineId, index) {
      this.phaseData.medicines[medicineId].periods.splice(index, 1)
    },
    addMedicine(categoryId) {
      const id = uuidv4()
      const name = this.medicineModels[categoryId].name
      const category = this.$util.copy(this.medicinesByCategory[categoryId].category)

      this.$set(this.periodModels, id, this.genNewPeriodModel())
      this.medicines.push({
        id,
        name,
        category,
      })

      this.medicineModels[categoryId] = this.genNewMedicineModel(true)

      this.$refs.addMedicineForm.forEach((form) => form.resetValidation())
    },
    buildPeriodString(period) {
      let string = ''
      if (!period.start && !period.dosage) {
        string = 'Período e dosagem não informados.'
        return string
      }

      let recurrence = 'por semana'
      if (period.period === 'DIAS') {
        recurrence = 'por dia'
      } else if (period.period === 'CADA_N_SEMANAS') {
        recurrence = `a cada ${period.length} `
        recurrence += period.length > 1 ? 'semanas' : 'semana'
      }

      if (!period.start) {
        string = `${period.dosage}mg ${recurrence} sem período informado.`
        return string
      }

      string =
        (period.dosage ? `${period.dosage}mg ${recurrence}, ` : 'Uso ') +
        `de ${this.$util.toFormattedDate(period.start)} até ${this.$util.toFormattedDate(period.end) || 'atualmente'}`

      if (period.stopReason) {
        string += ` - Motivo da parada: ${period.stopReason.name ?? period.stopReason ?? 'não informado'}`
      }

      if (period.events.selected?.length > 0) {
        string += ` ${
          period.events.selected.length > 1 ? 'com eventos adversos:' : 'com evento adverso:'
        } ${period.events.selected.join(', ')} ${
          period.events.date ? 'na data ' + period.events.date : 'sem data informada'
        }`
      }

      return string
    },
    async toggleMedicine(medicine, value) {
      if (value) {
        this.$set(this.phaseData.medicines, medicine.id, { medicine, periods: [] })
      } else {
        if (this.phaseData.medicines[medicine.id]?.periods?.length > 0) {
          if (!(await this.$root.confirm('Os períodos de uso deste medicamento serão apagados. Deseja continuar?'))) {
            return
          }
        }

        this.$delete(this.phaseData.medicines, medicine.id)
      }
    },
    async loadData() {
      try {
        this.loading = true

        const medicineStopReasons = await this.$api.getMedicineStopReasons()
        const medicinesForDiseaseTreatment = await this.$api.getMedicinesForDiseaseTreatment(this.attendanceDiseaseId)
        const adverseEffects = await this.$api.listCrud('crud/adverseEffect')

        this.treatments = (await this.$api.getTreatmentsForDisease(this.attendanceDiseaseId)).items

        this.stopReasons = medicineStopReasons
        const medicines = medicinesForDiseaseTreatment.items.filter(
          (m) => m.category.id !== MEDICINE_CATEGORIES.FOTOTERAPICOS,
        )
        this.mergeMedicines(medicines)
        this.adverseEffects = adverseEffects.items.map((item) => item.name)
        this.error = false
      } catch (error) {
        this.$root.error(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    mergeMedicines(medicines) {
      const medicineIdsSet = new Set(medicines.map((medicine) => medicine.id))
      this.medicines = medicines.concat(
        Object.values(this.phaseData.medicines)
          .filter((phaseDataMedicine) => !medicineIdsSet.has(phaseDataMedicine.medicine.id))
          .map((selectedMedicine) => selectedMedicine.medicine),
      )
      this.medicines.forEach((medicine) => this.$set(this.periodModels, medicine.id, this.genNewPeriodModel()))
      this.sortedCategoryKeys.forEach((categoryId) =>
        this.$set(this.medicineModels, categoryId, this.genNewMedicineModel()),
      )
    },
    genNewPeriodModel() {
      return {
        valid: false,
        dosage: null,
        stopReason: [],
        period: 'POR_SEMANA',
        length: null,
        start: null,
        end: null,
      }
    },
    genNewMedicineModel(valid = false) {
      return {
        valid,
        name: null,
      }
    },
  },
  mounted() {
    this.loadData()
  },
}
</script>

<style scoped></style>
